import { useCallback, useRef, useState } from 'preact/hooks';
import { ApiError, api } from '../../api/client';
import type {
  HmiAssetDto,
  ListHmiAssetsResponse,
} from '../../api/types';
import { useAsync } from '../../hooks/useAsync';
import { showConfirm } from '../Confirmer';
import './HmiAssetPicker.css';

/**
 * Reusable widget-config helper: shows the tenant's uploaded HMI assets
 * (SVG schematics + raster images) in a thumbnail grid, lets the operator
 * pick one, upload a new one, or delete an existing one. Controlled
 * component — `value` is the currently-selected asset id, `onChange` is
 * called on selection (with the new id) or on delete of the currently
 * selected one (with `null`).
 *
 * <p>Deliberately does not include the enclosing modal chrome — drops
 * cleanly into any widget-config editor that already has its own panel.
 * Layout: thumbnail per asset, filename + size below, delete affordance
 * on hover / focus. Upload button opens the file picker filtered to the
 * server's accepted MIME set.</p>
 */
export interface HmiAssetPickerProps {
  /** Currently-selected asset id, or null when nothing is chosen. */
  value: string | null;
  onChange(assetId: string | null): void;
  /** Optional CSS class hook for host-side layout. */
  class?: string;
}

const ACCEPTED_MIME = 'image/svg+xml,image/png,image/jpeg,image/webp';

export function HmiAssetPicker(props: HmiAssetPickerProps) {
  const { value, onChange } = props;

  // Bump this counter after an upload / delete so useAsync refetches.
  const [refreshTick, setRefreshTick] = useState(0);
  const state = useAsync(
    (signal) => api.get<ListHmiAssetsResponse>('/api/hmi-assets', signal),
    [refreshTick],
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const bumpAfterMutation = useCallback(() => setRefreshTick((n) => n + 1), []);

  const onPickFile = useCallback(() => {
    setUploadError(null);
    fileInputRef.current?.click();
  }, []);

  const onFileSelected = useCallback(async (ev: Event) => {
    const target = ev.currentTarget as HTMLInputElement;
    const file = target.files?.[0];
    // Reset the input so picking the SAME file twice in a row still fires
    // the change event.
    target.value = '';
    if (!file) return;

    setUploading(true);
    setUploadError(null);
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      const response = await fetch('/api/hmi-assets', {
        method: 'POST',
        body: form,
        credentials: 'include',
      });
      if (!response.ok) {
        let msg = `Upload failed (HTTP ${response.status})`;
        if (response.status === 413) msg = 'Datei zu groß (max. 2 MB).';
        if (response.status === 415) msg = 'Dateityp wird nicht unterstützt.';
        if (response.status === 400) {
          try {
            const body = await response.json() as { error?: string };
            if (body?.error) msg = body.error;
          } catch { /* body may be non-JSON */ }
        }
        throw new Error(msg);
      }
      const uploaded = await response.json() as HmiAssetDto;
      bumpAfterMutation();
      onChange(uploaded.id);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  }, [bumpAfterMutation, onChange]);

  const onDelete = useCallback(async (asset: HmiAssetDto) => {
    const ok = await showConfirm({
      title: 'Asset löschen?',
      message: `„${asset.displayName}" (${asset.kind}) wird entfernt. Widgets die dieses Asset referenzieren zeigen anschliessend einen Platzhalter.`,
      confirmLabel: 'Löschen',
      danger: true,
    });
    if (!ok) return;
    setDeletingId(asset.id);
    setDeleteError(null);
    try {
      await api.delete<void>(`/api/hmi-assets/${asset.id}`);
      // If the deleted one was selected, clear the selection.
      if (asset.id === value) onChange(null);
      bumpAfterMutation();
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.status === 403 ? 'Keine Berechtigung.' : `Fehler (HTTP ${err.status})`)
        : (err instanceof Error ? err.message : String(err));
      setDeleteError(msg);
    } finally {
      setDeletingId(null);
    }
  }, [bumpAfterMutation, onChange, value]);

  const rootClass = 'hv-asset-picker' + (props.class ? ` ${props.class}` : '');

  return (
    <div class={rootClass}>
      <div class="hv-asset-picker__toolbar">
        <button
          type="button"
          class="hv-asset-picker__upload"
          onClick={onPickFile}
          disabled={uploading}
        >
          {uploading ? 'Wird hochgeladen…' : 'Asset hochladen'}
        </button>
        {value && (
          <button
            type="button"
            class="hv-asset-picker__clear"
            onClick={() => onChange(null)}
          >
            Auswahl entfernen
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_MIME}
          class="hv-asset-picker__hidden-input"
          onChange={onFileSelected}
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>

      {uploadError && (
        <p class="hv-asset-picker__error" role="alert">{uploadError}</p>
      )}
      {deleteError && (
        <p class="hv-asset-picker__error" role="alert">{deleteError}</p>
      )}

      {state.status === 'loading' && (
        <p class="hv-asset-picker__muted">Assets werden geladen…</p>
      )}
      {state.status === 'error' && (
        <p class="hv-asset-picker__error">
          Assets konnten nicht geladen werden.
        </p>
      )}
      {state.status === 'ok' && state.data.assets.length === 0 && (
        <p class="hv-asset-picker__muted">
          Noch keine Assets in diesem Datenbereich. Lade zum Start eine SVG-
          oder Bild-Datei hoch.
        </p>
      )}

      {state.status === 'ok' && state.data.assets.length > 0 && (
        <ul class="hv-asset-picker__grid" role="listbox">
          {state.data.assets.map((asset) => (
            <li
              key={asset.id}
              class={
                'hv-asset-picker__tile'
                + (asset.id === value ? ' hv-asset-picker__tile--selected' : '')
              }
            >
              <button
                type="button"
                class="hv-asset-picker__tile-btn"
                onClick={() => onChange(asset.id)}
                role="option"
                aria-selected={asset.id === value}
                title={asset.displayName}
              >
                <span class="hv-asset-picker__thumb">
                  <img
                    src={`/api/hmi-assets/${asset.id}/content`}
                    alt=""
                    loading="lazy"
                  />
                </span>
                <span class="hv-asset-picker__meta">
                  <span class="hv-asset-picker__name">{asset.displayName}</span>
                  <span class="hv-asset-picker__size">
                    {formatSize(asset.sizeBytes)}
                  </span>
                </span>
              </button>
              <button
                type="button"
                class="hv-asset-picker__delete"
                onClick={() => onDelete(asset)}
                disabled={deletingId === asset.id}
                aria-label={`„${asset.displayName}" löschen`}
                title="Löschen"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
}
